/**
 * Dashboard Hooks
 * Custom hooks for manufacturing dashboard data fetching
 *
 * @description
 * Provides React Query hooks for:
 * - Dashboard KPI data
 * - Production chart data
 * - Payment chart data
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-3
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { format } from 'date-fns';
import manufacturingApi, {
  DashboardKpiResponse,
  ChartDataResponse,
  PaymentChartResponse,
  ManufacturingApiError
} from '@/services/manufacturing/manufacturingApi';

// ============================================
// HOOK: Dashboard KPIs
// ============================================

interface DashboardKpiFilters {
  from: Date;
  to: Date;
}

/**
 * Hook to fetch dashboard KPI data
 * @param filters - Date range filters
 * @returns Query result with KPI data
 */
export function useDashboardKpis(
  filters: DashboardKpiFilters
): UseQueryResult<DashboardKpiResponse, ManufacturingApiError> {
  return useQuery({
    queryKey: ['dashboard-kpis', filters],
    queryFn: async () => {
      return manufacturingApi.getDashboardKpis({
        from: format(filters.from, 'yyyy-MM-dd'),
        to: format(filters.to, 'yyyy-MM-dd')
      });
    },
    staleTime: 1000 * 30, // 30 seconds - auto-refetch after this
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 1000 * 30, // Auto-refetch every 30 seconds
    refetchIntervalInBackground: true, // Keep refetching even when tab is in background
  });
}

// ============================================
// HOOK: Production Chart
// ============================================

interface ProductionChartFilters {
  from: Date;
  to: Date;
  interval: 'DAILY' | 'WEEKLY' | 'MONTHLY';
}

/**
 * Hook to fetch production chart data
 * @param filters - Date range and interval filters
 * @returns Query result with chart data
 */
export function useProductionChart(
  filters: ProductionChartFilters
): UseQueryResult<ChartDataResponse, ManufacturingApiError> {
  return useQuery({
    queryKey: ['production-chart', filters],
    queryFn: async () => {
      return manufacturingApi.getProductionChart({
        from: format(filters.from, 'yyyy-MM-dd'),
        to: format(filters.to, 'yyyy-MM-dd'),
        interval: filters.interval
      });
    },
    staleTime: 1000 * 30, // 30 seconds - auto-refetch after this
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 1000 * 30, // Auto-refetch every 30 seconds
    refetchIntervalInBackground: true, // Keep refetching even when tab is in background
  });
}

// ============================================
// HOOK: Payment Chart
// ============================================

interface PaymentChartFilters {
  from: Date;
  to: Date;
  interval: 'DAILY' | 'WEEKLY' | 'MONTHLY';
}

/**
 * Hook to fetch payment chart data
 * @param filters - Date range and interval filters
 * @returns Query result with payment chart data and summary
 */
export function usePaymentChart(
  filters: PaymentChartFilters
): UseQueryResult<PaymentChartResponse, ManufacturingApiError> {
  return useQuery({
    queryKey: ['payment-chart', filters],
    queryFn: async () => {
      return manufacturingApi.getPaymentChart({
        from: format(filters.from, 'yyyy-MM-dd'),
        to: format(filters.to, 'yyyy-MM-dd'),
        interval: filters.interval
      });
    },
    staleTime: 1000 * 30, // 30 seconds - auto-refetch after this
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 1000 * 30, // Auto-refetch every 30 seconds
    refetchIntervalInBackground: true, // Keep refetching even when tab is in background
  });
}
